/**
 * Utility functions for planning fragmentation
 */

/**
 * Converts hours to HH:MM format
 * @param {number} hours - Duration in hours
 * @returns {string} Formatted duration (e.g., "2h30min")
 */
export const toHHMM = (hours) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return `${h}h${m.toString().padStart(2, '0')}min`
}

/**
 * Rounds a number to the nearest quarter (0.25)
 * @param {number} x - Number to round
 * @returns {number} Rounded value to nearest 0.25
 */
export const roundQuarter = (x) => Math.round(x * 4) / 4

/**
 * Validates input parameters for fragmentation
 * @param {Object} params - Input parameters
 * @param {number} params.totalWeeklyTime - Total weekly hours
 * @param {number} params.minSession - Minimum session duration
 * @param {number} params.maxSession - Maximum session duration
 * @param {Array} params.subjects - Array of subjects
 * @throws {Error} If parameters are invalid
 */
export const validateParameters = ({ totalWeeklyTime, minSession, maxSession, subjects }) => {
  if (totalWeeklyTime <= 0) {
    throw new Error('totalWeeklyTime must be greater than 0')
  }

  if (minSession <= 0) {
    throw new Error('minSession must be greater than 0')
  }

  if (maxSession <= minSession) {
    throw new Error('maxSession must be greater than minSession')
  }

  if (!subjects || subjects.length === 0) {
    throw new Error('subjects array cannot be empty')
  }

  const totalPerSubject = totalWeeklyTime / subjects.length
  const minSessionsNeeded = Math.ceil(totalPerSubject / maxSession)

  if (minSessionsNeeded * minSession > totalPerSubject) {
    throw new Error(
      `Impossible to distribute ${totalPerSubject}h with constraints min=${minSession}h, max=${maxSession}h`
    )
  }
}

/**
 * Calculates the sum of an array of numbers and rounds to quarter
 * @param {number[]} arr - Array of numbers
 * @returns {number} Sum rounded to nearest 0.25
 */
export const sumAndRound = (arr) => {
  return roundQuarter(arr.reduce((a, b) => a + b, 0))
}

export const formatHours = (hours) => {
  const totalMinutes = Math.round(hours * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60

  if (h === 0) {
    return `${m}min`
  }
  if (m === 0) {
    return `${h}h`
  }
  return `${h}h ${m}min`
}

export const getStatusLabel = (status) => {
  const statusMap = {
    pending: 'Pendente',
    done: 'Concluída',
    in_progress: 'Em andamento',
  }
  return statusMap[status] || status
}

export const generateID = (max = 10) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let id = ''
  for (let i = 0; i < max; i += 1) {
    const randomIndex = Math.floor(Math.random() * characters.length)
    id += characters[randomIndex]
  }
  return id
}

/**
 * Generates a suggested name for a study plan
 * Format: NOME DO USUÁRIO - SIGLA DO CONCURSO - CODE
 * @param {string} userName - User's name
 * @param {string} contestName - Contest name (may contain "SIGLA - NOME DO ÓRGÃO")
 * @returns {string} Suggested plan name
 */
export const generateSuggestedPlanName = (userName, contestName) => {
  if (!userName || !contestName) {
    return ''
  }

  // Extract contest acronym: if contest name follows "SIGLA - NOME DO ÓRGÃO" pattern,
  // get the part before " - ", otherwise use the full name
  let contestAcronym = contestName.trim()
  if (contestName.includes(' - ')) {
    contestAcronym = contestName.split(' - ')[0].trim()
  }

  // Generate a random code
  const code = generateID(8)

  // Build the suggested name
  return `${userName} - ${contestAcronym} - ${code}`.trim().toUpperCase()
}
